import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, orderBy, addDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import AISummary from '../../components/AISummary';
import CoachPerformance from '../../components/CoachPerformance';
import LearnerMetrics from '../../components/LearnerMetrics';
import CanvasCoachView from '../../components/CanvasCoachView';
import ReportRenderer from '../../components/ReportRenderer';

const STAGE_COLORS = {
  'Problem-Solution Fit':        '#1d4ed8',
  'Product-Market Fit':          '#0369a1',
  'Early Growth & Monetization': '#166534',
  'Scaling & Regional Expansion':'#065f46',
};

const STAGES = [
  'Problem-Solution Fit',
  'Product-Market Fit',
  'Early Growth & Monetization',
  'Scaling & Regional Expansion',
];

export default function ClientDashboard() {
  const { profile } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [allPrograms, setAllPrograms] = useState([]);
  const [allCohorts, setAllCohorts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const [filterProgramId, setFilterProgramId] = useState('all');
  const [filterCohortId, setFilterCohortId]   = useState('all');

  const [view, setView] = useState(() => {
    const hash = window.location.hash.replace('#','');
    return ['overview','companies','coaches','documents','reports'].includes(hash) ? hash : 'overview';
  });

  useEffect(() => {
    const onHash = () => {
      const hash = window.location.hash.replace('#','');
      if (['overview','companies','coaches','documents','reports'].includes(hash)) {
        setView(hash);
        setSelectedCompany(null);
      }
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    async function load() {
      try {
        const programIds = profile?.programIds || [];
        if (programIds.length === 0) {
          setCompanies([]); setAllPrograms([]); setAllCohorts([]);
          setLoading(false);
          return;
        }
        const [compSnap, progSnap, cohortSnap] = await Promise.all([
          getDocs(query(collection(db, 'companies'), where('programId', 'in', programIds))),
          getDocs(query(collection(db, 'programs'), where('__name__', 'in', programIds))),
          getDocs(query(collection(db, 'cohorts'), where('programId', 'in', programIds))),
        ]);
        setCompanies(compSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setAllPrograms(progSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setAllCohorts(cohortSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) { setError(err.message); }
      finally { setLoading(false); }
    }
    if (profile?.uid) load();
  }, [profile]);

  const handleViewChange = (newView) => {
    setView(newView);
    setSelectedCompany(null);
    window.location.hash = newView;
  };
  const handleCompanyClick = (c) => { setSelectedCompany(c); setView('company'); };

  const filteredCompanies = companies.filter(c => {
    if (filterCohortId !== 'all') return c.cohortId === filterCohortId;
    if (filterProgramId !== 'all') return c.programId === filterProgramId;
    return true;
  });

  const visibleCohorts = filterProgramId === 'all'
    ? allCohorts
    : allCohorts.filter(co => co.programId === filterProgramId);

  const headerSubtitle = (() => {
    if (filterCohortId !== 'all') return allCohorts.find(c => c.id === filterCohortId)?.name || 'Selected cohort';
    if (filterProgramId !== 'all') return allPrograms.find(p => p.id === filterProgramId)?.name || 'Selected programme';
    return 'All programmes';
  })();

  const stageDistribution = STAGES.map(stage => ({
    stage, count: filteredCompanies.filter(c => c.currentStage === stage).length, color: STAGE_COLORS[stage],
  })).filter(s => s.count > 0);

  const advancedCompanies = filteredCompanies.filter(c => c.latestStage && c.latestStage !== c.currentStage).length;

  const relevantMetricCohorts = allCohorts.filter(co => {
    const hasTarget = co.targets?.primaryMetric?.avgPerCompany;
    if (!hasTarget) return false;
    if (filterCohortId !== 'all') return co.id === filterCohortId;
    if (filterProgramId !== 'all') return co.programId === filterProgramId;
    return true;
  });
  const metricGroups = {};
  relevantMetricCohorts.forEach(co => {
    const label = co.targets?.primaryMetric?.label || 'Primary metric';
    if (!metricGroups[label]) metricGroups[label] = [];
    metricGroups[label].push(co.id);
  });

  return (
    <DashboardLayout>
      <div className="gm-page">
        <div className="gm-page-header">
          <div>
            <p className="gm-page-welcome">Welcome, {profile?.displayName}</p>
            <h1 className="gm-page-title">Programme Overview</h1>
            <p className="gm-page-sub">{headerSubtitle}</p>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8, alignItems:'flex-end' }}>
            <ClientProgrammeSelector
              programs={allPrograms} cohorts={visibleCohorts}
              filterProgramId={filterProgramId}
              setFilterProgramId={(v) => { setFilterProgramId(v); setFilterCohortId('all'); }}
              filterCohortId={filterCohortId} setFilterCohortId={setFilterCohortId}
            />
            <div className="gm-client-tabs">
              {['overview','companies','coaches','documents','reports'].map(v => (
                <button key={v}
                  className={`gm-client-tab ${(view === v || (v === 'overview' && view === 'company')) ? 'gm-client-tab--active' : ''}`}
                  onClick={() => handleViewChange(v)}>
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading && <div className="gm-loading"><div className="gm-spinner" /></div>}
        {error && <div className="gm-alert gm-alert-error">{error}</div>}

        {!loading && !error && view === 'overview' && !selectedCompany && (
          <>
            <div className="gm-kpi-row">
              <div className="gm-kpi-card">
                <div className="gm-kpi-value">{filteredCompanies.length}</div>
                <div className="gm-kpi-label">Participating companies</div>
              </div>
              <div className="gm-kpi-card gm-kpi-card--accent">
                <div className="gm-kpi-value">{advancedCompanies}</div>
                <div className="gm-kpi-label">Companies advanced a stage</div>
              </div>
              <div className="gm-kpi-card">
                <div className="gm-kpi-value">{filteredCompanies.filter(c => c.latestStage).length}</div>
                <div className="gm-kpi-label">Companies with assessments</div>
              </div>
            </div>

            <div className="gm-section">
              <h2 className="gm-section-title">Stage distribution</h2>
              <div className="gm-stage-bars">
                {stageDistribution.map(({ stage, count, color }) => (
                  <div key={stage} className="gm-stage-bar-row">
                    <div className="gm-stage-bar-label">{stage}</div>
                    <div className="gm-stage-bar-track">
                      <div className="gm-stage-bar-fill"
                        style={{ width: `${filteredCompanies.length > 0 ? (count/filteredCompanies.length)*100 : 0}%`, background: color }} />
                    </div>
                    <div className="gm-stage-bar-count">{count}</div>
                  </div>
                ))}
              </div>
            </div>

            {Object.entries(metricGroups).map(([label, ids]) => (
              <div className="gm-section" key={label}>
                <LearnerMetrics
                  cohortId={filterCohortId !== 'all' ? filterCohortId : undefined}
                  cohortIds={filterCohortId === 'all' ? ids : undefined}
                  isEditable={false}
                />
              </div>
            ))}

            <div className="gm-section">
              <h2 className="gm-section-title">Companies — click to view detail</h2>
              <div className="gm-company-grid">
                {filteredCompanies
                  .sort((a,b) => STAGES.indexOf(b.currentStage) - STAGES.indexOf(a.currentStage))
                  .map(c => {
                    const color = STAGE_COLORS[c.currentStage] || '#4a5481';
                    return (
                      <div key={c.id} className="gm-company-card gm-company-card--clickable"
                        onClick={() => handleCompanyClick(c)}>
                        <div className="gm-company-card-header">
                          <div className="gm-company-card-name">{c.name}</div>
                        </div>
                        <p className="gm-company-card-desc">{c.description?.slice(0,80)}{c.description?.length > 80 ? '…' : ''}</p>
                        <div className="gm-client-stage-card" style={{ borderColor: color }}>
                          <div className="gm-client-stage-label">Current stage</div>
                          <div className="gm-client-stage-name" style={{ color }}>{c.currentStage || 'Not yet assessed'}</div>
                          <div className="gm-client-stage-sub">{c.currentStageUpgrade} · {c.currentStageProgress}% through stage</div>
                          <div className="gm-mini-progress" style={{ marginTop: 8 }}>
                            <div className="gm-mini-progress-fill"
                              style={{ width: `${c.currentStageProgress||0}%`, background: color }} />
                          </div>
                        </div>
                        <div className="gm-client-stage-target">
                          Target: <span style={{ color: STAGE_COLORS[c.targetStage], fontWeight: 600 }}>{c.targetStage || '—'}</span>
                        </div>
                      </div>
                    );
                  })}
                {filteredCompanies.length === 0 && (
                  <div className="gm-empty-state"><p>No companies in this scope yet.</p></div>
                )}
              </div>
            </div>
          </>
        )}

        {!loading && !error && view === 'companies' && !selectedCompany && (
          <div className="gm-section">
            <h2 className="gm-section-title">Companies — click to view detail</h2>
            <div className="gm-company-grid">
              {filteredCompanies
                .sort((a,b) => STAGES.indexOf(b.currentStage) - STAGES.indexOf(a.currentStage))
                .map(c => {
                  const color = STAGE_COLORS[c.currentStage] || '#4a5481';
                  return (
                    <div key={c.id} className="gm-company-card gm-company-card--clickable"
                      onClick={() => handleCompanyClick(c)}>
                      <div className="gm-company-card-header">
                        <div className="gm-company-card-name">{c.name}</div>
                      </div>
                      <p className="gm-company-card-desc">{c.description?.slice(0,80)}{c.description?.length > 80 ? '…' : ''}</p>
                      <div className="gm-client-stage-card" style={{ borderColor: color }}>
                        <div className="gm-client-stage-label">Current stage</div>
                        <div className="gm-client-stage-name" style={{ color }}>{c.currentStage || 'Not yet assessed'}</div>
                        <div className="gm-client-stage-sub">{c.currentStageUpgrade} · {c.currentStageProgress}% through stage</div>
                      </div>
                    </div>
                  );
                })}
              {filteredCompanies.length === 0 && (
                <div className="gm-empty-state"><p>No companies in this scope yet.</p></div>
              )}
            </div>
          </div>
        )}

        {!loading && view === 'company' && selectedCompany && (
          <CompanyDetail company={selectedCompany} onBack={() => { setSelectedCompany(null); setView('companies'); }} />
        )}

        {!loading && !error && view === 'coaches' && (
          <div className="gm-section">
            <h2 className="gm-section-title">Coach performance</h2>
            <CoachPerformanceList companies={filteredCompanies} />
          </div>
        )}

        {!loading && !error && view === 'documents' && (
          <div className="gm-section">
            <h2 className="gm-section-title">Strategic documents</h2>
            <p style={{ fontSize: '0.82rem', color: '#6b6b6b', marginBottom: 20 }}>
              Select a company to view its Business Model Canvas and Customer & User Journey Map, including full version history.
            </p>
            <DocumentsBrowser companies={filteredCompanies} profile={profile} />
          </div>
        )}

        {!loading && !error && view === 'reports' && (
          <ClientReportsView
            companies={filteredCompanies}
            headerSubtitle={headerSubtitle}
            filterProgramId={filterProgramId}
            filterCohortId={filterCohortId}
            clientId={profile?.clientId}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

function ClientProgrammeSelector({ programs, cohorts, filterProgramId, setFilterProgramId, filterCohortId, setFilterCohortId }) {
  if (programs.length === 0) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {programs.length > 1 && (
        <select value={filterProgramId} onChange={e => setFilterProgramId(e.target.value)}
          style={{ padding: '6px 10px', border: '1.5px solid var(--gm-primary-light)', borderRadius: 6, fontSize: '0.78rem', fontFamily: 'inherit', color: 'var(--gm-primary)', background: 'var(--gm-primary-faint)', cursor: 'pointer' }}>
          <option value="all">All programmes</option>
          {programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      )}
      {filterProgramId !== 'all' && cohorts.length > 1 && (
        <select value={filterCohortId} onChange={e => setFilterCohortId(e.target.value)}
          style={{ padding: '6px 10px', border: '1.5px solid var(--gm-primary-light)', borderRadius: 6, fontSize: '0.78rem', fontFamily: 'inherit', color: 'var(--gm-primary)', background: 'var(--gm-primary-faint)', cursor: 'pointer' }}>
          <option value="all">All cohorts</option>
          {cohorts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      )}
      {(filterProgramId !== 'all' || filterCohortId !== 'all') && (
        <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '0.72rem' }}
          onClick={() => { setFilterProgramId('all'); setFilterCohortId('all'); }}>
          ✕ Clear
        </button>
      )}
    </div>
  );
}

function CoachPerformanceList({ companies }) {
  const coachMap = {};
  companies.forEach(c => {
    if (c.coachId && c.coachName) {
      coachMap[c.coachId] = { id: c.coachId, uid: c.coachId, displayName: c.coachName };
    }
  });
  const coaches = Object.values(coachMap);

  if (coaches.length === 0) return <div className="gm-empty-state"><p>No coaches assigned to companies in this scope yet.</p></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {coaches.map(c => (
        <CoachPerformance key={c.uid} coachId={c.uid} coachName={c.displayName} showAI={true} scopedCompanies={companies} />
      ))}
    </div>
  );
}

function DocumentsBrowser({ companies, profile }) {
  const [selected, setSelected] = useState(null);

  if (companies.length === 0) return <div className="gm-empty-state"><p>No companies in this scope yet.</p></div>;

  if (selected) {
    return (
      <div>
        <button className="gm-back-btn" onClick={() => setSelected(null)}>← All companies</button>
        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <h3 className="gm-page-title" style={{ fontSize: '1.1rem' }}>{selected.name}</h3>
        </div>
        <CanvasCoachView companyId={selected.id} profile={profile} />
      </div>
    );
  }

  return (
    <div className="gm-company-grid">
      {companies.map(c => (
        <div key={c.id} className="gm-company-card gm-company-card--clickable" onClick={() => setSelected(c)}>
          <div className="gm-company-card-header">
            <div className="gm-company-card-name">{c.name}</div>
          </div>
          <p className="gm-company-card-desc">{c.description?.slice(0,100)}{c.description?.length > 100 ? '…' : ''}</p>
        </div>
      ))}
    </div>
  );
}

function CompanyDetail({ company, onBack }) {
  const [sessions, setSessions]   = useState([]);
  const [workPlans, setWorkPlans] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    async function load() {
      try {
        const [sSnap, wpSnap] = await Promise.all([
          getDocs(query(collection(db, 'companies', company.id, 'coachingSessions'), orderBy('sessionDate', 'desc'))),
          getDocs(collection(db, 'companies', company.id, 'workPlans')),
        ]);
        setSessions(sSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setWorkPlans(wpSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    load();
  }, [company.id]);

  const openActions = sessions.flatMap(s =>
    (s.actionItems||[]).filter(a => a.status === 'open').map(a => ({ ...a, sessionDate: s.sessionDate }))
  );

  const latestSession = sessions[0];
  const lastSessionDate = latestSession?.sessionDate?.toDate
    ? latestSession.sessionDate.toDate().toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })
    : null;

  return (
    <div>
      <button className="gm-back-btn" onClick={onBack}>← All companies</button>

      <div style={{ marginTop: 16, marginBottom: 24 }}>
        <h2 className="gm-page-title">{company.name}</h2>
        <p className="gm-page-sub">{company.description}</p>
      </div>

      <div className="gm-stage-compare" style={{ marginBottom: 24 }}>
        <div className="gm-stage-compare-card">
          <div className="gm-stage-compare-label">Baseline</div>
          <div className="gm-stage-compare-stage" style={{ color: STAGE_COLORS[company.currentStage] }}>{company.currentStage || '—'}</div>
          <div className="gm-stage-compare-upgrade">{company.currentStageUpgrade}</div>
          <div className="gm-mini-progress" style={{ marginTop: 8 }}>
            <div className="gm-mini-progress-fill"
              style={{ width: `${company.currentStageProgress||0}%`, background: STAGE_COLORS[company.currentStage] }} />
          </div>
          <div className="gm-mini-progress-label">{company.currentStageProgress}% through stage</div>
        </div>
        {company.latestStage && (
          <div className="gm-stage-compare-card gm-stage-compare-card--current">
            <div className="gm-stage-compare-label">Latest assessment</div>
            <div className="gm-stage-compare-stage" style={{ color: STAGE_COLORS[company.latestStage] }}>{company.latestStage}</div>
            <div className="gm-stage-compare-upgrade">{company.latestStageUpgrade}</div>
            <div className="gm-mini-progress" style={{ marginTop: 8 }}>
              <div className="gm-mini-progress-fill"
                style={{ width: `${company.latestStageProgress||0}%`, background: STAGE_COLORS[company.latestStage] }} />
            </div>
            <div className="gm-mini-progress-label">{company.latestStageProgress}% through stage</div>
          </div>
        )}
        <div className="gm-stage-compare-card gm-stage-compare-card--target">
          <div className="gm-stage-compare-label">Target</div>
          <div className="gm-stage-compare-stage" style={{ color: STAGE_COLORS[company.targetStage] }}>{company.targetStage || '—'}</div>
          <div className="gm-stage-compare-upgrade">{company.targetStageUpgrade}</div>
          <div className="gm-mini-progress" style={{ marginTop: 8 }}>
            <div className="gm-mini-progress-fill"
              style={{ width: `${company.targetStageProgress||0}%`, background: STAGE_COLORS[company.targetStage] }} />
          </div>
          <div className="gm-mini-progress-label">{company.targetStageProgress}% through stage</div>
        </div>
        <div className="gm-kpi-card">
          <div className="gm-kpi-value">{sessions.length}</div>
          <div className="gm-kpi-label">Sessions held</div>
          {lastSessionDate && <div className="gm-kpi-sub">Last: {lastSessionDate}</div>}
        </div>
        <div className="gm-kpi-card">
          <div className="gm-kpi-value" style={{ color: openActions.length > 0 ? '#dc2626' : '#166534' }}>
            {openActions.length}
          </div>
          <div className="gm-kpi-label">Open actions</div>
        </div>
      </div>

      <div className="gm-client-tabs" style={{ marginBottom: 24 }}>
        {['summary','sessions','workplans','documents'].map(tab => (
          <button key={tab}
            className={`gm-client-tab ${activeTab === tab ? 'gm-client-tab--active' : ''}`}
            onClick={() => setActiveTab(tab)}>
            {tab === 'summary' ? 'AI Summary' : tab === 'sessions' ? 'Sessions' : tab === 'workplans' ? 'Work Plans' : 'Documents'}
          </button>
        ))}
      </div>

      {loading && <div className="gm-loading"><div className="gm-spinner" /></div>}

      {!loading && (
        <>
          {activeTab === 'summary' && (
            <AISummary companyId={company.id} companyName={company.name} role="client" />
          )}

          {activeTab === 'sessions' && (
            <div>
              {sessions.length === 0 && <div className="gm-empty-state"><p>No sessions logged yet.</p></div>}
              {sessions.map(s => {
                const date = s.sessionDate?.toDate ? s.sessionDate.toDate() : new Date(s.sessionDate);
                const openCount = (s.actionItems||[]).filter(a => a.status === 'open').length;
                return (
                  <div key={s.id} className="gm-session-card" style={{ marginBottom: 12 }}>
                    <div className="gm-session-card-header" style={{ cursor: 'default' }}>
                      <div className="gm-session-card-left">
                        <div className="gm-session-date">{date.toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })}</div>
                        <div className="gm-session-meta">
                          {s.durationMins && <span>{s.durationMins} mins</span>}
                          {openCount > 0 && <span className="gm-session-open-count">{openCount} open action{openCount > 1 ? 's' : ''}</span>}
                        </div>
                        <p className="gm-session-preview">{s.keyPoints?.slice(0,200)}{s.keyPoints?.length > 200 ? '…' : ''}</p>
                        {s.decisions && <p style={{ fontSize: '0.78rem', color: '#6b6b6b', marginTop: 4 }}><strong>Decisions:</strong> {s.decisions.slice(0,150)}{s.decisions.length > 150 ? '…' : ''}</p>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'workplans' && (
            <WorkPlansBySession workPlans={workPlans} sessions={sessions} />
          )}

          {activeTab === 'documents' && (
            <DocumentsBrowser companies={[company]} profile={null} />
          )}
        </>
      )}
    </div>
  );
}

function WorkPlansBySession({ workPlans, sessions }) {
  const [expandedSessions, setExpandedSessions] = useState({});
  const STATUS_COLORS = { draft:'#6b6b6b', agreed:'#0369a1', reported:'#166534', reviewed:'#065f46' };

  if (workPlans.length === 0) return <div className="gm-empty-state"><p>No work plans recorded yet.</p></div>;

  const bySession = {};
  workPlans.forEach(p => {
    if (!bySession[p.sessionId]) bySession[p.sessionId] = [];
    bySession[p.sessionId].push(p);
  });

  const sessionIds = Object.keys(bySession);

  return (
    <div>
      {sessionIds.map(sessionId => {
        const session = sessions.find(s => s.id === sessionId);
        const plans   = bySession[sessionId];
        const date    = session?.sessionDate?.toDate
          ? session.sessionDate.toDate().toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })
          : 'Session';
        const isOpen  = expandedSessions[sessionId];
        const reviewedCount = plans.filter(p => p.status === 'reviewed').length;

        return (
          <div key={sessionId} className="gm-session-card" style={{ marginBottom: 8 }}>
            <div className="gm-session-card-header"
              onClick={() => setExpandedSessions(prev => ({ ...prev, [sessionId]: !prev[sessionId] }))}>
              <div className="gm-session-card-left">
                <div className="gm-session-date">Work plans — {date}</div>
                <div className="gm-session-meta">
                  <span>{plans.length} bucket{plans.length !== 1 ? 's' : ''}</span>
                  <span style={{ color: reviewedCount === plans.length ? '#166534' : '#0369a1' }}>
                    {reviewedCount}/{plans.length} reviewed
                  </span>
                </div>
              </div>
              <span className="gm-session-toggle">{isOpen ? '▲' : '▼'}</span>
            </div>
            {isOpen && (
              <div className="gm-session-card-body">
                {plans.map(p => (
                  <div key={p.id} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                      <strong style={{ fontSize: '0.85rem' }}>{p.bucketLabel || p.bucketId}</strong>
                      <span className="gm-workplan-status" style={{ background: (STATUS_COLORS[p.status]||'#6b6b6b') + '22', color: STATUS_COLORS[p.status]||'#6b6b6b' }}>
                        {p.status || 'draft'}
                      </span>
                    </div>
                    {p.plan && <div style={{ marginBottom: 6 }}><span style={{ fontSize:'0.72rem', fontWeight:700, textTransform:'uppercase', color:'#94a3b8' }}>Plan</span><p style={{ fontSize:'0.85rem', margin:'4px 0 0' }}>{p.plan}</p></div>}
                    {p.outcome && <div style={{ marginBottom: 6 }}><span style={{ fontSize:'0.72rem', fontWeight:700, textTransform:'uppercase', color:'#94a3b8' }}>Outcome</span><p style={{ fontSize:'0.85rem', margin:'4px 0 0' }}>{p.outcome}</p></div>}
                    {p.scalingLink && <div style={{ marginBottom: 6 }}><span style={{ fontSize:'0.72rem', fontWeight:700, textTransform:'uppercase', color:'#94a3b8' }}>Scaling impact</span><p style={{ fontSize:'0.85rem', margin:'4px 0 0' }}>{p.scalingLink}</p></div>}
                    {p.metrics && Object.entries(p.metrics).filter(([,v])=>v).map(([k,v]) => (
                      <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.78rem', padding:'3px 0', borderBottom:'1px solid #f8f9fa' }}>
                        <span style={{ color:'#6b6b6b' }}>{k}</span><span style={{ fontWeight:700 }}>{v}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ClientReportsView({ companies, headerSubtitle, filterProgramId, filterCohortId, clientId }) {
  const [generating, setGenerating] = useState(false);
  const [report, setReport]         = useState(null);
  const [editing, setEditing]       = useState(false);
  const [editDraft, setEditDraft]   = useState('');
  const [saving, setSaving]         = useState(false);
  const [savedReport, setSavedReport] = useState(null);
  const [saveError, setSaveError]   = useState(null);
  const [historicalAssessments, setHistoricalAssessments] = useState([]);
  const [loadingTrends, setLoadingTrends] = useState(false);

  const stageOrder = Object.fromEntries(STAGES.map((s,i) => [s,i]));
  const sorted = [...companies].sort((a,b) => stageOrder[b.currentStage] - stageOrder[a.currentStage]);
  const needsAttention = companies.filter(c => STAGES.indexOf(c.currentStage) < STAGES.indexOf(c.targetStage) - 1);
  const onTarget = companies.length - needsAttention.length;

  const loadTrends = async () => {
    setLoadingTrends(true);
    try {
      const allAssessments = [];
      for (const c of companies) {
        const snap = await getDocs(collection(db, 'companies', c.id, 'assessments'));
        snap.docs.forEach(d => allAssessments.push({ ...d.data(), companyId: c.id, companyName: c.name }));
      }
      allAssessments.sort((a,b) => {
        const da = a.assessmentDate?.toDate ? a.assessmentDate.toDate() : new Date(a.assessmentDate||0);
        const db2 = b.assessmentDate?.toDate ? b.assessmentDate.toDate() : new Date(b.assessmentDate||0);
        return da - db2;
      });
      setHistoricalAssessments(allAssessments);
    } catch (err) { console.error(err); }
    finally { setLoadingTrends(false); }
  };

  useEffect(() => { loadTrends(); }, [companies.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateReport = async () => {
    setGenerating(true);
    try {
      const functions = getFunctions(undefined, 'europe-west1');
      const fn = httpsCallable(functions, 'generateAISummary');

      const trendsByCompany = {};
      historicalAssessments.forEach(a => {
        if (!trendsByCompany[a.companyId]) trendsByCompany[a.companyId] = [];
        trendsByCompany[a.companyId].push((a.type || 'assessment') + ': ' + a.stage + ' (' + a.stageProgress + '%)');
      });
      const trendLines = Object.entries(trendsByCompany)
        .map(([cId, entries]) => {
          const name = companies.find(c => c.id === cId)?.name || cId;
          return name + ' — ' + entries.join(' → ');
        }).join('\n');

      const companySummary = companies.map(c =>
        c.name + ' — ' + (c.currentStage||'Not assessed') + ' (' + (c.currentStageUpgrade||'') + ', ' + (c.currentStageProgress||0) + '% through stage) → Target: ' + (c.targetStage||'—')
      ).join('\n');

      const stageDistribution = STAGES.map(stage => ({
        stage, count: companies.filter(c => c.currentStage === stage).length
      }));

      const prompt =
        'You are a programme analyst preparing a management report for a client overseeing an enterprise scaling programme: ' + headerSubtitle + '.\n\n' +
        'COMPANIES IN SCOPE: ' + companies.length + '\n\n' +
        'STAGE DISTRIBUTION:\n' +
        stageDistribution.map(s => s.stage + ': ' + s.count + ' companies').join('\n') + '\n\n' +
        'CURRENT COMPANY STATUS:\n' + companySummary + '\n\n' +
        'STAGE PROGRESSION HISTORY (trend data — earliest to latest per company):\n' + (trendLines || 'No historical assessment data available yet.') + '\n\n' +
        'Write a structured management report covering:\n' +
        '1. Executive summary (2-3 sentences)\n' +
        '2. Stage distribution and overall portfolio health\n' +
        '3. Trend analysis — which companies are progressing well over time, which are stagnant or regressing, based on the stage progression history provided\n' +
        '4. Highlights — companies making strong progress\n' +
        '5. Areas of concern — companies needing acceleration\n' +
        '6. Recommendations for the client\'s consideration in the coming period\n\n' +
        'Write professionally for a funder/client audience. Use clear headings. Be specific and data-driven. Do not fabricate data not provided — if trend data is sparse, say so honestly rather than inventing patterns.';

      const result = await fn({ prompt, companyId: 'client_report' });
      const generatedText = result.data?.summary || 'Report generation failed.';
      setReport(generatedText);
      setEditDraft(generatedText);
      setEditing(false);
    } catch (err) { console.error(err); setReport('Failed to generate report. Please try again.'); }
    finally { setGenerating(false); }
  };

  const printReport = () => window.print();

  return (
    <div>
      <div className="gm-section gm-report-generate-panel">
        <h2 className="gm-section-title">Programme report</h2>
        <div className="gm-config-card">
          <div className="gm-config-label">Generate report — {headerSubtitle}</div>
          <p className="gm-config-desc">
            AI-generated management report covering stage distribution, trend analysis across assessment history, coach performance, and recommendations.
            Scoped strictly to your selected programme/cohort. Editable before saving, printable as PDF.
          </p>
          <div style={{ display:'flex', gap:12, marginTop:12, flexWrap:'wrap' }}>
            <button className="gm-btn gm-btn-primary" onClick={generateReport} disabled={generating || loadingTrends}>
              {generating ? 'Generating…' : loadingTrends ? 'Loading trend data…' : report ? '↻ Regenerate' : '✦ Generate report'}
            </button>
            {report && !editing && (
              <button className="gm-btn gm-btn-outline" onClick={() => { setEditing(true); setEditDraft(savedReport || report); }}>
                ✏️ Edit report
              </button>
            )}
            {(savedReport || report) && !editing && (
              <button className="gm-btn gm-btn-outline" onClick={printReport}>
                🖨 Print / Save as PDF
              </button>
            )}
          </div>
        </div>
      </div>

      {report && (
        <div className="gm-section gm-report-output" id="client-report">
          <div className="gm-report-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
            <div>
              <h2>{headerSubtitle}</h2>
              <p>{savedReport ? 'Final version saved' : 'AI draft — not yet saved'}</p>
            </div>
            {savedReport && !editing && <span className="gm-validated-badge">✓ Final version saved</span>}
          </div>

          {editing ? (
            <div style={{ marginTop:16 }}>
              <p style={{ fontSize:'0.78rem', color:'#6b6b6b', marginBottom:8 }}>
                Edit the report below before saving the final version.
              </p>
              {saveError && (
                <div className="gm-alert gm-alert-error" style={{ marginBottom: 12 }}>{saveError}</div>
              )}
              <textarea
                value={editDraft}
                onChange={e => setEditDraft(e.target.value)}
                style={{ width:'100%', minHeight:400, padding:'16px', border:'1.5px solid #0369a1', borderRadius:8, fontSize:'0.875rem', fontFamily:'inherit', lineHeight:1.7, outline:'none', resize:'vertical', boxSizing:'border-box' }}
              />
              <div style={{ display:'flex', gap:12, marginTop:12 }}>
                <button className="gm-btn gm-btn-primary" disabled={saving}
                  onClick={async () => {
                    setSaving(true);
                    setSaveError(null);
                    try {
                      await addDoc(collection(db, 'reports'), {
                        type: 'client_programme',
                        programId: filterProgramId !== 'all' ? filterProgramId : null,
                        cohortId: filterCohortId !== 'all' ? filterCohortId : null,
                        clientId: clientId || null,
                        scopeLabel: headerSubtitle,
                        content: editDraft,
                        savedBy: 'client',
                        createdAt: serverTimestamp(),
                      });
                      setSavedReport(editDraft);
                      setReport(editDraft);
                      setEditing(false);
                    } catch(err) {
                      console.error('Failed to save client report:', err);
                      setSaveError(err.message || 'Failed to save report. Please try again.');
                    } finally { setSaving(false); }
                  }}>
                  {saving ? 'Saving…' : '✓ Save final version'}
                </button>
                <button className="gm-btn gm-btn-outline" onClick={() => setEditing(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <div className="gm-ai-summary-body">
              <ReportRenderer text={savedReport || report} />
            </div>
          )}

          <div style={{ marginTop:32 }}>
            <h3 style={{ fontSize:'0.95rem', fontWeight:700, marginBottom:12 }}>Company status</h3>
            <table className="gm-table">
              <thead>
                <tr><th>Company</th><th>Current stage</th><th>Progress</th><th>Target</th><th>Status</th></tr>
              </thead>
              <tbody>
                {sorted.map(c => {
                  const color = STAGE_COLORS[c.currentStage] || '#4a5481';
                  const onTrack = STAGES.indexOf(c.currentStage) >= STAGES.indexOf(c.targetStage) - 1;
                  return (
                    <tr key={c.id}>
                      <td style={{ fontWeight:600 }}>{c.name}</td>
                      <td><span style={{ color, fontWeight:600, fontSize:'0.82rem' }}>{c.currentStage || '—'}</span></td>
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <div className="gm-mini-progress" style={{ width:70 }}>
                            <div className="gm-mini-progress-fill" style={{ width:`${c.currentStageProgress||0}%`, background:color }} />
                          </div>
                          <span style={{ fontSize:'0.72rem', color:'#6b6b6b' }}>{c.currentStageUpgrade}</span>
                        </div>
                      </td>
                      <td style={{ fontSize:'0.78rem' }}>{c.targetStage || '—'}</td>
                      <td>
                        {onTrack
                          ? <span className="gm-validated-badge">On track</span>
                          : <span className="gm-pending-badge" style={{ background:'#fef2f2', color:'#dc2626', borderColor:'#fecaca' }}>Needs acceleration</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
      {historicalAssessments.length > 0 && (
        <div className="gm-section">
          <h3 className="gm-section-title">Stage progression by company</h3>
          <div className="gm-table-wrap">
            <table className="gm-table">
              <thead><tr><th>Company</th><th>Assessment history</th></tr></thead>
              <tbody>
                {companies.map(c => {
                  const history = historicalAssessments.filter(a => a.companyId === c.id);
                  if (history.length === 0) return null;
                  return (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600 }}>{c.name}</td>
                      <td style={{ fontSize: '0.82rem' }}>
                        {history.map((a, i) => (
                          <span key={i} style={{ marginRight: 8 }}>
                            <span style={{ color: STAGE_COLORS[a.stage], fontWeight: 600 }}>{a.stage}</span>
                            {i < history.length - 1 && <span style={{ color: '#94a3b8' }}> →</span>}
                          </span>
                        ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

          </div>
        </div>
      )}

      {!report && (
        <div className="gm-kpi-row" style={{ marginBottom: 32 }}>
          <div className="gm-kpi-card">
            <div className="gm-kpi-value" style={{ color: '#166534' }}>{onTarget}</div>
            <div className="gm-kpi-label">Companies on track</div>
          </div>
          <div className="gm-kpi-card">
            <div className="gm-kpi-value" style={{ color: needsAttention.length > 0 ? '#dc2626' : '#166534' }}>{needsAttention.length}</div>
            <div className="gm-kpi-label">Need acceleration</div>
          </div>
          <div className="gm-kpi-card">
            <div className="gm-kpi-value">{companies.length}</div>
            <div className="gm-kpi-label">Total companies</div>
          </div>
        </div>
      )}
    </div>
  );
}
